import FloatingChatProvider from "@/components/providers/floatingChat";
import { useApolloClient, useMutation } from "@apollo/client/react";
import MontuToastProvider from "@repo/components/src/globalProviders/toast";
import { Toast, ToastDescription } from "@repo/components/src/ui/toast";
import { hasReachedLimit } from "@/utils/freeTrial";
import { CreateChatDocument } from "@repo/graphql/documents/chat/mutation.generated";
import { WorkspaceFreeTrialDocument } from "@repo/graphql/documents/workspace/query.generated";
import { FreeTrialStatus } from "@repo/graphql/gql/graphql";
import { useCallback } from "react";

type OpenChatInput = {
  brandId: string;
  userId: string;
  /** Runs once the chat exists, right before the floating chat is shown. */
  onOpened?: () => void;
};

type OpenChatsInput = {
  brandId: string;
  userIds: string[];
};

type UseOpenCreatorChatResult = {
  /** Opens (creating it if needed) the chat with one creator. Resolves to the
   *  chat id, or null when the backend refused it (the failure is toasted). */
  openChat: (input: OpenChatInput) => Promise<string | null>;
  /** Bulk variant: every chat is created independently, so a refusal of one
   *  doesn't block the others. Shows the floating panel on the first chat that
   *  was created. */
  openChats: (input: OpenChatsInput) => Promise<void>;
  loading: boolean;
};

const showErrorToast = (message: string, duration: number) => {
  MontuToastProvider.show({
    placement: "bottom",
    duration,
    render: ({ id }) => (
      <Toast nativeID={`toast-${id}`} action="error" variant="solid">
        <ToastDescription size="sm">{message}</ToastDescription>
      </Toast>
    ),
  });
};

/** Opens the chat with a creator. `createChat` is get-or-create on the backend:
 *  an existing chat is returned without any quota check and only a new one runs
 *  the CHATS ability check. So the free trial's limit is never pre-checked here
 *  (that would block reopening chats the brand already has); it only decides
 *  which message to show when the mutation is refused. */
export const useOpenCreatorChat = (): UseOpenCreatorChatResult => {
  const client = useApolloClient();
  const [createChat, { loading }] = useMutation(CreateChatDocument);

  const refetchTrial = useCallback(async () => {
    try {
      await client.query({
        query: WorkspaceFreeTrialDocument,
        fetchPolicy: "network-only",
      });
    } catch {
      // The counters are informative; a failed refresh must not break chatting.
    }
  }, [client]);

  // Reads the trial fresh, only when a chat was refused, so rows that render
  // dozens of chat buttons never fire a request per row.
  const showChatFailure = useCallback(async () => {
    try {
      const { data } = await client.query({
        query: WorkspaceFreeTrialDocument,
        fetchPolicy: "network-only",
      });
      const trial =
        data?.workspace.myWorkspace?.workspace.workspaceplan.freeTrial;
      if (
        trial?.status === FreeTrialStatus.Active &&
        hasReachedLimit(trial.chatsUsed, trial.chatsLimit)
      ) {
        const limitLabel =
          trial.chatsLimit != null ? `${trial.chatsLimit} chats` : "chats";
        showErrorToast(
          `Llegaste al límite de ${limitLabel} de la prueba gratis. Elige un plan para chatear con más creadores.`,
          5000,
        );
        return;
      }
    } catch {
      // Fall through to the generic message.
    }
    showErrorToast("No pudimos abrir el chat. Inténtalo nuevamente.", 4000);
  }, [client]);

  const create = useCallback(
    async (brandId: string, userId: string): Promise<string> => {
      const result = await createChat({
        variables: { chat: { brandId, userId } },
      });
      const chatId = result.data?.chat.createChat.chat.id;
      if (!chatId) throw new Error("createChat returned no chat");
      return chatId;
    },
    [createChat],
  );

  const openChat = useCallback(
    async (input: OpenChatInput): Promise<string | null> => {
      try {
        const chatId = await create(input.brandId, input.userId);
        void refetchTrial();
        input.onOpened?.();
        FloatingChatProvider.show({ chatId });
        return chatId;
      } catch {
        await showChatFailure();
        return null;
      }
    },
    [create, refetchTrial, showChatFailure],
  );

  const openChats = useCallback(
    async (input: OpenChatsInput): Promise<void> => {
      const results = await Promise.allSettled(
        input.userIds.map((userId) => create(input.brandId, userId)),
      );
      const chatIds = results.flatMap((result) =>
        result.status === "fulfilled" ? [result.value] : [],
      );
      const [firstChatId] = chatIds;
      if (firstChatId) {
        void refetchTrial();
        FloatingChatProvider.show({ chatId: firstChatId });
      }
      if (chatIds.length < results.length) await showChatFailure();
    },
    [create, refetchTrial, showChatFailure],
  );

  return { openChat, openChats, loading };
};
